import { createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { LOGIN_ENABLED } from '@/lib/features';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface AuthUser {
  id:           string;
  name:         string;
  phoneNumber:  string;
  email?:       string | null;
  role:         'admin' | 'user';
  createdAt:    Date;
}

interface AuthCtx {
  user:              AuthUser | null;
  isLoading:         boolean;
  isAuthenticated:   boolean;
  isAdmin:           boolean;
  /** Level-1 admin OR Level-2 instructor — i.e. can access the admin area. */
  isStaff:           boolean;
  /** True only for Level-2 instructor accounts. */
  isInstructor:      boolean;
  /** Redirect the browser to /login (phone OTP flow lives there). */
  signIn:            (redirectTo?: string) => void;
  signOut:           () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────
export const AuthContext = createContext<AuthCtx | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  // auth.me is the single tRPC query that drives all auth state.
  const { data: user, isLoading } = trpc.auth.me.useQuery(undefined, {
    retry:       false,
    staleTime:   60_000,
    refetchOnWindowFocus: true,
  });

  /**
   * Take the user to /login. We don't open a popup or call any provider —
   * the LoginPage handles phone + WhatsApp OTP itself.
   */
  const signIn = (redirectTo = '/') => {
    if (!LOGIN_ENABLED) {
      window.alert('Sign-in is temporarily unavailable.');
      return;
    }
    const url = redirectTo !== '/'
      ? `/login?redirect=${encodeURIComponent(redirectTo)}`
      : '/login';
    window.location.href = url;
  };

  /**
   * Hit the server sign-out route. The server destroys the session and
   * redirects back to /. We clear the React Query cache first so no stale
   * user data is shown to the next person on a shared device.
   */
  const signOut = () => {
    queryClient.clear();
    window.location.href = '/auth/signout';
  };

  const value: AuthCtx = {
    user:            (user as AuthUser | null | undefined) ?? null,
    isLoading,
    isAuthenticated: !!user,
    isAdmin:         user?.role === 'admin',
    isStaff:         user?.role === 'admin' || user?.role === 'instructor',
    isInstructor:    user?.role === 'instructor',
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthProvider>');
  return ctx;
}
